// 帳票の本文を様式の登録簿（fieldMeta）から汎用に描く。読み取り専用。
// 未登録の様式は、本文のキーをそのまま見出しにして出す。
import { fieldMeta, type FieldMeta } from '@alfine/shared/forms';
import { Muted } from './ui';

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0);
}

function scalar(meta: FieldMeta | undefined, v: unknown): string {
  if (typeof v === 'boolean') return v ? 'はい' : 'いいえ';
  if (meta?.kind === 'enum') return meta.options?.find((o) => o.value === String(v))?.label ?? String(v);
  return String(v);
}

function Value({ meta, value }: { meta?: FieldMeta; value: unknown }) {
  if (isEmpty(value)) return <span className="text-ink-4">未記入</span>;
  if (Array.isArray(value)) {
    return (
      <ol className="space-y-2">
        {value.map((item, i) => (
          <li key={i} className="rounded-md border border-line p-2">
            <Value meta={meta?.item} value={item} />
          </li>
        ))}
      </ol>
    );
  }
  if (typeof value === 'object') {
    return <Fields fields={meta?.children} value={value as Record<string, unknown>} nested />;
  }
  return <span className="whitespace-pre-wrap">{scalar(meta, value)}</span>;
}

function Fields({ fields, value, nested }: { fields?: FieldMeta[]; value: Record<string, unknown>; nested?: boolean }) {
  // 様式に無いキー（または子の定義が無い対応表）も落とさず出す
  const known = fields ?? [];
  const extra = Object.keys(value).filter((k) => !known.some((f) => f.key === k));
  const rows: Array<{ key: string; label: string; meta?: FieldMeta }> = [...known.map((f) => ({ key: f.key, label: f.label, meta: f })), ...extra.map((k) => ({ key: k, label: k }))];
  return (
    <dl className={nested ? 'space-y-1.5' : 'space-y-3'}>
      {rows.map((r) => (
        <div key={r.key} className={nested ? 'grid gap-1 sm:grid-cols-[10rem_1fr]' : 'space-y-0.5'}>
          <dt className="text-sm text-ink-3">{r.label}</dt>
          <dd className="text-ink-1">
            <Value meta={r.meta} value={value[r.key]} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function DocumentBody({ docType, body }: { docType: string; body: Record<string, unknown> }) {
  let fields: FieldMeta[] | undefined;
  try {
    fields = fieldMeta(docType);
  } catch {
    fields = undefined;
  }
  if (isEmpty(body)) return <Muted>本文がありません。</Muted>;
  return <Fields fields={fields} value={body} />;
}
